import React from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Button, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@material-ui/core';
import { Edit } from '@material-ui/icons';
import { greens, greys, white } from '@/styles/colors';
import { sansSerif } from '@/styles/typography';
import EditFieldsModal from './modals/EditFieldsModal';
import { contactPatch } from './utils';

const NAVY = '#1E1B4B';
const AVATAR_INDIGO = '#4F46E5';
const SURFACE = '#F4F5F7';
const HEADER_GREY = '#8A8F98';

export const PageSurface = styled.div`
    ${sansSerif}
    background: ${SURFACE};
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    gap: 1.25rem;
    min-height: 0;
    overflow-y: auto;
    padding: 2rem 2.5rem 3rem;
    /* A tab row that already spaces its content sets --tab-top-padding (CorporateCarrier's CarrierTabs). */
    padding-top: var(--tab-top-padding, 2rem);
`;

/* --------------------------------- banner --------------------------------- */

export const AccountBanner = styled.div`
    align-items: center;
    background: ${NAVY};
    border-radius: 8px;
    color: ${white.primary};
    display: flex;
    justify-content: space-between;
    padding: 20px 24px;
`;

export const BannerLeft = styled.div`
    align-items: center;
    display: flex;
    gap: 16px;
`;

export const BannerAvatar = styled.div`
    align-items: center;
    background: ${AVATAR_INDIGO};
    border-radius: 8px;
    display: flex;
    flex-shrink: 0;
    font-size: 16px;
    font-weight: 700;
    height: 46px;
    justify-content: center;
    width: 46px;
`;

export const BannerEyebrow = styled.span`
    color: rgba(255, 255, 255, 0.65);
    display: block;
    font-size: 12px;
    font-weight: 600;
`;

export const BannerName = styled.span`
    display: block;
    font-size: 20px;
    font-weight: 800;
    line-height: 1.3;
`;

export const BannerMeta = styled.span`
    color: rgba(255, 255, 255, 0.75);
    display: block;
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.4px;
    text-transform: uppercase;
`;

export const BannerRight = styled.div`
    align-items: flex-end;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

export const BannerActions = styled.div`
    display: flex;
    gap: 10px;
`;

export const BannerButton = styled(Button)`
    ${sansSerif}
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.25);
    border-radius: 6px;
    color: ${white.primary};
    font-size: 12px;
    font-weight: 700;
    min-width: 0;
    padding: 5px 14px;
    text-transform: none;

    &:hover {
        background: rgba(255, 255, 255, 0.22);
    }
`;

/* ------------------------------- tab strip -------------------------------- */

export const TabStrip = styled.div`
    background: #e8e9ec;
    border-radius: 9px;
    display: inline-flex;
    gap: 2px;
    padding: 4px;
`;

export const TabButton = styled.button`
    ${sansSerif}
    background: ${({ selected }) => (selected ? greens.primary : 'transparent')};
    border: none;
    border-radius: 6px;
    color: ${({ selected }) => (selected ? white.primary : greys.secondary)};
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
    padding: 6px 14px;
    white-space: nowrap;

    &:hover {
        background: ${({ selected }) => (selected ? greens.primary : 'rgba(0, 0, 0, 0.05)')};
    }
`;

/* -------------------------------- sections -------------------------------- */

export const Section = styled.section`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const SectionHeader = styled.div`
    align-items: center;
    display: flex;
    gap: 11px;
`;

export const AccentBar = styled.span`
    background: ${greens.primary};
    border-radius: 2px;
    flex-shrink: 0;
    height: 16px;
    width: 3px;
`;

export const SectionTitle = styled.span`
    color: #0c0b1d;
    font-size: 15px;
    font-weight: 700;
`;

export const SectionNote = styled.span`
    color: ${greys.subtitle};
    font-size: 12.5px;
`;

export const Card = styled.div`
    background: ${white.primary};
    border: 1px solid ${greys.border};
    border-radius: 8px;
    overflow: hidden;
`;

export const CardBody = styled.div`
    color: ${greys.secondary};
    font-size: 13.5px;
    padding: 16px 20px;
`;

export const CardRow = styled.div`
    align-items: center;
    display: flex;
    justify-content: space-between;
    padding: 16px 20px;
`;

/* ------------------------------- data tables ------------------------------ */

export const DataHeaderCell = styled(TableCell)`
    background: ${white.secondary};
    border-bottom: 1px solid ${greys.border};
    color: ${HEADER_GREY};
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.6px;
    padding: 12px 16px;
    text-transform: uppercase;
    white-space: nowrap;
`;

export const DataCell = styled(TableCell)`
    border-bottom: 1px solid ${greys.border};
    color: ${greys.secondary};
    font-size: 13px;
    padding: 14px 16px;
`;

export const StrongCell = styled.span`
    color: #0c0b1d;
    font-size: 13.5px;
    font-weight: 700;
`;

export const SubCell = styled.span`
    color: ${greys.subtitle};
    display: block;
    font-size: 12px;
    margin-top: 3px;
`;

/* --------------------------------- chips ---------------------------------- */

export const StatusPill = styled.span`
    align-items: center;
    background: ${({ tone }) => (tone === 'on' ? '#E7F6EA' : tone === 'bad' ? '#FDEAEA' : '#EFEFEF')};
    border-radius: 999px;
    color: ${({ tone }) => (tone === 'on' ? '#2E7D3A' : tone === 'bad' ? '#C0392B' : greys.secondary)};
    display: inline-flex;
    font-size: 11px;
    font-weight: 700;
    gap: 5px;
    letter-spacing: 0.3px;
    padding: 3px 10px;
    text-transform: uppercase;

    &::before {
        background: currentColor;
        border-radius: 50%;
        content: '';
        height: 5px;
        width: 5px;
    }
`;

/* ------------------------------ routing cards ----------------------------- */

export const RoutingGrid = styled.div`
    display: grid;
    gap: 20px;
    grid-template-columns: 1fr 1fr;
`;

export const RoutingCard = styled.div`
    background: ${white.primary};
    border: 1px solid ${({ active }) => (active ? greens.primary : greys.border)};
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 24px;
`;

export const RoutingIcon = styled.span`
    align-items: center;
    background: ${({ tone }) => (tone === 'green' ? greens.primary : '#4F46E5')};
    border-radius: 8px;
    color: ${white.primary};
    display: inline-flex;
    height: 28px;
    justify-content: center;
    width: 28px;

    .MuiSvgIcon-root {
        font-size: 15px;
    }
`;

export const WaterfallLabel = styled.span`
    color: ${HEADER_GREY};
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.6px;
    text-transform: uppercase;
`;

export const StepBadge = styled.span`
    align-items: center;
    border: 1px solid ${greens.primary};
    border-radius: 50%;
    color: ${greens.primary};
    display: inline-flex;
    flex-shrink: 0;
    font-size: 11px;
    font-weight: 700;
    height: 22px;
    justify-content: center;
    width: 22px;
`;

export const StepPill = styled.span`
    background: #e7f6ea;
    border-radius: 4px;
    color: #2e7d3a;
    font-size: 10px;
    font-weight: 700;
    padding: 2px 6px;
`;

export const EmptyState = styled.div`
    align-items: center;
    color: ${greys.primary};
    display: flex;
    font-size: 14px;
    justify-content: center;
    padding: 3rem 1rem;
    text-align: center;
`;

/* ----------------------------- composed blocks ----------------------------- */

export const Header = ({ title, note }) => (
    <SectionHeader>
        <AccentBar />
        <SectionTitle>{title}</SectionTitle>
        {note && <SectionNote>{note}</SectionNote>}
    </SectionHeader>
);

export const DataTable = ({ columns, children }) => (
    <Card>
        <Table>
            <TableHead>
                <TableRow>
                    {columns.map((label, i) => (
                        <DataHeaderCell key={label || `spacer-${i}`}>{label}</DataHeaderCell>
                    ))}
                    <DataHeaderCell align="right" />
                </TableRow>
            </TableHead>
            <TableBody>{children}</TableBody>
        </Table>
    </Card>
);

export const EditCell = ({ onClick, disabled, title, label }) => (
    <DataCell align="right">
        <Tooltip title={title || ''} placement="top">
            <span>
                <IconButton size="small" aria-label={label} onClick={onClick} disabled={disabled}>
                    <Edit
                        css={css`
                            font-size: 18px;
                        `}
                    />
                </IconButton>
            </span>
        </Tooltip>
    </DataCell>
);

// Hands `onSave` the UPDATE_CLIENT_BY_PK patch for `slot`, never the raw draft.
export const ContactEditModal = ({ open, slot, saving, error, onClose, onSave }) => (
    <EditFieldsModal
        open={open}
        title={`Edit contact — ${slot?.title || ''}`}
        fields={[
            ...(slot?.nameField ? [{ key: 'name', label: 'Name', required: slot.required }] : []),
            {
                key: 'email',
                label: 'Email',
                disabled: !!slot?.emailReadOnly,
                helperText: slot?.emailReadOnly
                    ? 'This is the login email. Admins cannot change account emails at this time.'
                    : undefined,
            },
            { key: 'phone', label: 'Phone', required: slot?.required },
        ]}
        values={{
            name: slot?.name || '',
            email: slot?.email || '',
            phone: slot?.phone || '',
        }}
        saving={saving}
        error={error}
        onClose={onClose}
        onSave={(draft) => onSave(contactPatch(slot, draft))}
    />
);
